import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Switch, Route, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'

import { BottomNav } from 'components/BottomNav'
import { Header, HeaderBook, HeaderLogin, HeaderProfile } from 'components/Header'
import { Footer } from 'components/Footer'
import { SchedulePopup, ReschedulePopup } from 'components/Popups'
import PrivateRoute from 'components/PrivateRoute'

import * as PAGES from './pages'

import { logIn, setIsPro } from 'store/actions'

import { authApi, profileApi } from 'api'

const App = (props) => {
  const location = useLocation()
  const pathname = location.pathname
  const pathSegments = pathname.split('/')
  const [logged, setLogged] = useState(true)
  const [isBookPage, setIsBookPage] = useState(false)
  const [isConfirmPage, setIsConfirmPage] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [, setIsLoading] = useState(false)

  const hiddenHeaderPaths = new Set([
    '/',
    '/pro/join',
    '/pro/join/',
    '/pro/signup',
    '/pro/signup/',
    '/pro/rate',
    '/pro/stripe',
    '/user/login',
    '/pro/join/standard',
    '/pro/join/standard/',
    '/pro/join/premium',
    '/pro/join/premium/',
    '/pro/login',
    '/pro/login-dev',
    '/pro/login/',
    '/user/join',
    '/user/confirm',
    '/dashboard/pro',
  ])

  const noHeaderModPaths = new Set([
    '/',
    '/pro/join',
    '/pro/join/',
    '/pro/signup',
    '/pro/signup/',
    '/pro/rate',
    '/pro/stripe',
    '/pro/join/standard',
    '/pro/join/standard/',
    '/pro/join/premium',
    '/pro/join/premium/',
    '/user/login',
    '/pro/login',
    '/pro/login-dev',
    '/pro/login/',
    '/user/join',
    '/dashboard/user',
    '/dashboard/pro',
  ])

  const footerHiddenPaths = new Set([
    '/',
    '/popup/schedule',
    '/popup/reschedule',
    '/user/join',
    '/pro/login',
    '/pro/login-dev',
    '/pro/login/',
    '/user/login',
    '/book/time-picker',
    '/dashboard/user',
    '/dashboard/pro',
    '/pro/join',
    '/pro/join/',
    '/pro/signup',
    '/pro/signup/',
    '/pro/join/standard',
    '/pro/join/standard/',
    '/pro/join/premium',
    '/pro/join/premium/',
    '/pro/rate',
    '/pro/stripe',
  ])

  const bottomNavHiddenPaths = new Set([
    '/',
    '/pro/join',
    '/pro/signup',
    '/pro/rate',
    '/dashboard/pro',
    '/user/login',
    '/pro/join/standard',
    '/pro/join/premium',
    '/pro/login',
    '/pro/login-dev',
    '/pro/stripe',
  ])

  const isSummaryRoute = pathname.includes('/summary/')
  const isTalkRoute = pathname.includes('/talk/')
  const isRoomRoute = pathname.includes('/room')
  const isEventRoute = pathname.includes('/event/')
  const isShareRoute = pathSegments.includes('share')

  const showProfileHeader =
    isBookPage || isConfirmPage || pathname === '/book/time-picker'
  const shouldHideMainHeader =
    hiddenHeaderPaths.has(pathname) ||
    isSummaryRoute ||
    isTalkRoute ||
    isRoomRoute ||
    isEventRoute ||
    isShareRoute ||
    isBookPage
  const showBookHeader = !shouldHideMainHeader && pathSegments.length === 2
  const shouldUseNoHeaderMod =
    noHeaderModPaths.has(pathname) ||
    isSummaryRoute ||
    isTalkRoute ||
    isShareRoute ||
    isRoomRoute ||
    isEventRoute ||
    ((isBookPage || isConfirmPage) && windowWidth < 1024)
  const shouldHideFooter =
    footerHiddenPaths.has(pathname) ||
    isBookPage ||
    isConfirmPage ||
    isSummaryRoute ||
    isShareRoute ||
    isTalkRoute ||
    isRoomRoute ||
    isEventRoute
  const shouldHideBottomNav =
    bottomNavHiddenPaths.has(pathname) ||
    isShareRoute ||
    isSummaryRoute ||
    isBookPage ||
    isConfirmPage
  const isProtectedPath =
    pathname.startsWith('/summary/') ||
    pathname.startsWith('/talk/') ||
    pathname.startsWith('/event/') ||
    pathname.startsWith('/account/') ||
    pathname.startsWith('/result') ||
    pathname.startsWith('/pro/share/') ||
    pathname === '/dashboard/user' ||
    pathname === '/request/confirmed' ||
    pathname === '/dashboard/pro' ||
    pathname === '/pro/stripe' ||
    pathname === '/pro/rate' ||
    pathname === '/pro/callback' ||
    pathname === '/popup/schedule' ||
    pathname === '/popup/reschedule'

  useLayoutEffect(() => {
    if (pathname === '/dashboard/pro') {
      document.body.classList.add('logged-in')
      return
    }

    document.body.classList.remove('logged-in')
  }, [pathname])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)

    const loadSession = async () => {
      let loggedIn = window.localStorage.getItem('token')

      if (loggedIn) {
        if (pathname === '/dashboard/pro') {
          document.body.classList.add('logged-in')
        }
      } else {
        document.body.classList.remove('logged-in')
      }

      if (loggedIn) {
        const validationRes = await authApi.validate(loggedIn)

        if (validationRes?.error) {
          const refreshToken = window.localStorage.getItem('refreshToken')
          const refreshRes = refreshToken
            ? await authApi.refresh({ refresh: refreshToken })
            : null

          const nextToken = refreshRes?.result?.access || refreshRes?.result?.access_token
          const nextRefreshToken =
            refreshRes?.result?.refresh || refreshRes?.result?.refresh_token

          if (nextToken) {
            window.localStorage.setItem('token', nextToken)
            if (nextRefreshToken) {
              window.localStorage.setItem('refreshToken', nextRefreshToken)
            }
            loggedIn = nextToken
          } else {
            window.localStorage.clear()
            if (isProtectedPath) {
              window.location.replace('https://join.talkpro.com')
            } else {
              setLogged(false)
            }
            return
          }
        }
      }

      if (loggedIn && logged) {
        if (JSON.parse(window.localStorage.getItem('isPro'))) {
          const profileRes = await profileApi.getProfileInfo()
          if (profileRes?.result?.profile) {
            props.logIn(profileRes?.result?.profile)
            props.setIsPro(true)
          }
        } else {
          const profileRes = await profileApi.getUserInfo()
          if (profileRes?.result?.profile) {
            props.logIn(profileRes?.result?.profile)
            props.setIsPro(false)
          } else {
            window.localStorage.clear()
            if (isProtectedPath) {
              window.location.replace('https://join.talkpro.com')
            } else {
              setLogged(false)
            }
            return
          }
        }
      }

      setLogged(false)
    }

    loadSession().catch(() => {
      if (window.localStorage.getItem('token')) {
        window.localStorage.clear()
        if (isProtectedPath) {
          window.location.replace('https://join.talkpro.com')
        } else {
          setLogged(false)
        }
      } else {
        setLogged(false)
      }
    })

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isProtectedPath, logged, props, pathname])

  return (
    <div className="wrapper">
      {logged && pathname === '/' ? (
        <HeaderLogin />
      ) : showProfileHeader ? (
        <HeaderProfile isBookPage={isBookPage} isConfirmPage={isConfirmPage} />
      ) : shouldHideMainHeader ? null : showBookHeader ? (
        <HeaderBook />
      ) : (
        <Header />
      )}
      <div className={`base ${shouldUseNoHeaderMod ? 'no_header_mod' : ''}`}>
        <div
          className={`${
            isBookPage || isConfirmPage ? 'section_wrap profile_mod' : 'section_wrap'
          }`}
        >
          <Switch location={location}>
            <Route
              exact
              path="/"
              component={() => {
                // FIXME: uncomment
                window.location.replace('https://join.talkpro.com')
                return null
              }}
            />
            <PrivateRoute component={PAGES.SummaryPage} path="/summary/group/:id" exact />
            <PrivateRoute
              component={PAGES.SummaryPage}
              path="/summary/private/:id"
              exact
            />
            <PrivateRoute component={PAGES.During} path="/talk/:id" exact />
            <Route component={PAGES.Huddle} path="/:slug/room/" exact />
            <PrivateRoute component={PAGES.Event} path="/event/:id" exact />
            <PrivateRoute
              component={PAGES.AccountSettings}
              path="/account/settings"
              exact
            />
            <PrivateRoute
              component={PAGES.AccountProfile}
              path="/account/profile"
              exact
            />
            <PrivateRoute component={PAGES.ResultPage} path="/result" exact />
            <PrivateRoute
              component={PAGES.SharePage}
              path="/pro/share/:merchant?"
              exact
            />
            {/* User paths */}
            <Route
              path="/:proId"
              exact
              render={(props) => (
                <PAGES.BookPage
                  {...props}
                  setIsLoading={setIsLoading}
                  setIsBookPage={setIsBookPage}
                />
              )}
            />
            <Route component={PAGES.JoinPage} path="/user/join:code?" exact />
            <Route component={PAGES.LoginPage} path="/user/login" exact />
            <Route component={PAGES.TimePickerPage} path="/book/time-picker" exact />
            <PrivateRoute component={PAGES.TuberPage} path="/dashboard/user" exact />
            <PrivateRoute component={PAGES.RequestSent} path="/request/confirmed" exact />
            <Route
              path="/user/confirm/:data?"
              exact
              render={(props) => (
                <PAGES.JoinConfirmPage {...props} setIsConfirmPage={setIsConfirmPage} />
              )}
            />
            <Route
              path="/user/payments"
              exact
              render={(props) => (
                <PAGES.PaymentsPage {...props} setIsConfirmPage={setIsConfirmPage} />
              )}
            />
            <Route
              path="/user/callback/:data?"
              exact
              render={(props) => (
                <PAGES.BookedPage {...props} setIsConfirmPage={setIsConfirmPage} />
              )}
            />
            {/* Pro paths */}
            <Route component={PAGES.SignupPage} path="/pro/join" exact />
            <Route component={PAGES.SignupPage} path="/pro/signup" exact />
            <Route
              component={PAGES.ProJoinStandardPage}
              path="/pro/join/standard"
              exact
            />
            <Route component={PAGES.ProJoinPremiumPage} path="/pro/join/premium" exact />
            <Route component={PAGES.RoleSelect} path="/pro/login" exact />
            <Route component={PAGES.RoleSelectDev} path="/pro/login-dev" exact />
            <Route component={PAGES.CancelPage} path="/cancel/call" exact />
            <PrivateRoute component={PAGES.TalkerPage} path="/dashboard/pro" exact />
            <PrivateRoute component={PAGES.StripePage} path="/pro/stripe" exact />
            <PrivateRoute component={PAGES.RateBpage} path="/pro/rate" exact />
            <PrivateRoute component={PAGES.Callback} path="/pro/callback" exact />
            <PrivateRoute component={SchedulePopup} path="/popup/schedule" exact />
            <PrivateRoute component={ReschedulePopup} path="/popup/reschedule" exact />
          </Switch>
          {shouldHideFooter ||
          (pathname === '/dashboard/user' && windowWidth < 1024) ? null : (
            <Footer />
          )}
        </div>
      </div>

      {shouldHideBottomNav ? null : windowWidth < 1024 ? <BottomNav /> : null}
    </div>
  )
}

const mapStateToProps = ({ auth: { user, attributes, isPro, isOnline, pro } }) => ({
  user,
  attributes,
  isPro,
  isOnline,
  pro,
})

const mapDispatchToProps = {
  logIn,
  setIsPro,
}

export default connect(mapStateToProps, mapDispatchToProps)(App)
